<script setup lang="ts">
  // Session movements of a caja (V2 design system). Derives the rows from the
  // pure domain module, resolves every label once per row, and switches the
  // layout by its own element width: two-line rows + actions dialog below
  // CAJA_MOVEMENTS_WIDE_MIN, one-line rows with inline action buttons above.
  import {
    computed,
    getCurrentInstance,
    onScopeDispose,
    ref,
    toRef,
    watch,
  } from 'vue';
  import { useElementSize, useNow } from '@vueuse/core';
  import { startOfDay } from 'date-fns';
  import {
    getCajaMovementRows,
    formatCajaMovementTime,
    ICajaMovementRow,
    ICajaMovementRowContext,
    TCajaMovementAction,
  } from '@/domain/caja/caja-movement-row';
  import { CAJA_MOVEMENTS_WIDE_MIN } from '@/domain/pos/pos-compaction';
  import { PermissionType } from '@/enums/PermissionType';
  import { ICaja } from '@/interfaces/ICaja';
  import { ICajaMovement } from '@/interfaces/ICajaMovement';
  import { useAuth } from '@/composition/use-auth';
  import { useCajaMovementActions } from '@/composition/use-caja-movement-actions';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import V2CajaMovementActionsModal from './V2CajaMovementActionsModal.vue';
  import V2CajaMovementRow from './V2CajaMovementRow.vue';
  import type {
    ICajaMovementActionView,
    ICajaMovementRowView,
  } from './V2CajaMovementRow.vue';

  const props = defineProps<{
    movements: ICajaMovement[];
    caja: ICaja;
    /** The caller is refetching after `changed`: rows are stale meanwhile. */
    refreshing: boolean;
  }>();

  const emit = defineEmits<{
    /** A movement was refunded or re-tendered: the caller must refetch. */
    (e: 'changed'): void;
  }>();

  const auth = useAuth();
  const modals = useModal();
  const money = usePosMoney();
  const { busy: running, run } = useCajaMovementActions(toRef(props, 'caja'));
  const dialogOpen = ref(false);
  let disposed = false;
  onScopeDispose(() => {
    disposed = true;
  });
  // No row may open a second dialog or start a money action while the actions
  // dialog is up, one runs, or the just-changed rows are being refetched (a
  // struck row would still offer "Reembolsar").
  const busy = computed(
    () => dialogOpen.value || running.value || props.refreshing
  );

  // Rows show the date only for movements before today, so they re-derive
  // when the local day flips, not every minute: `views` depends on `today`,
  // which the clock only updates at midnight. Sessions reach thousands of
  // movements; re-rendering them all each minute would freeze the page.
  const now = useNow({ interval: 60_000 });
  const today = ref(startOfDay(now.value));
  watch(now, (current) => {
    const day = startOfDay(current);
    if (day.getTime() !== today.value.getTime()) today.value = day;
  });

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  // Width-only measurement (see CAJA_MOVEMENTS_WIDE_MIN): the on-screen
  // keyboard changes height, never width, so the tier can't flip under it.
  // Border box, not content box: this element scrolls, and on classic-
  // scrollbar desktops the content box loses the scrollbar's width when the
  // rows overflow. Wide rows are taller than narrow ones, so near the
  // threshold the content-box width would flip the tier, which flips the
  // overflow, which flips the width again, every frame.
  const rootRef = ref<HTMLElement | null>(null);
  const { width: listWidth } = useElementSize(rootRef, undefined, {
    box: 'border-box',
  });
  const wide = computed(() => listWidth.value >= CAJA_MOVEMENTS_WIDE_MIN);

  const ctx = computed<ICajaMovementRowContext>(() => ({
    canManage: auth.hasPermission(PermissionType.CajaManage),
    deviceBound: props.caja.posDeviceId != null,
    cajaOpen: props.caja.status === 'open',
  }));

  const rows = computed(() => getCajaMovementRows(props.movements, ctx.value));

  type TActionSkin = Pick<
    ICajaMovementActionView,
    'icon' | 'color' | 'variant'
  >;
  const ACTION_SKINS: Record<TCajaMovementAction, TActionSkin> = {
    viewOrders: { icon: 'receipt', color: 'neutral', variant: 'outline' },
    changeMethod: {
      icon: 'swap-horizontal',
      color: 'neutral',
      variant: 'outline',
    },
    refund: { icon: 'cash-refund', color: 'error', variant: 'soft' },
  };

  function actionLabel(
    action: TCajaMovementAction,
    row: ICajaMovementRow
  ): string {
    switch (action) {
      case 'viewOrders':
        return String(proxy.$tc('cajaV2.movementViewOrders', row.ordersCount));
      case 'changeMethod':
        return i18nT('cajaV2.movementChangeMethod');
      case 'refund':
        return i18nT('posV2.paymentsRefund');
    }
  }

  function toView(row: ICajaMovementRow): ICajaMovementRowView {
    const timeText = formatCajaMovementTime(
      row.createdAt,
      today.value,
      proxy.$i18n.locale
    );
    const ordersText = row.ordersCount
      ? String(
          proxy.$tc('cajaV2.movementOrders', row.ordersCount, {
            count: row.ordersCount,
          })
        )
      : null;
    return {
      ...row,
      typeLabel: i18nT(row.typeKey),
      methodLabel: row.hasMethod
        ? row.bucket
          ? i18nT(`posV2.method.${row.bucket}`)
          : i18nT('posV2.methodOther')
        : null,
      timeText,
      amountText: `${row.sign}${money.formatComputedMoney(row.amount)}`,
      metaText: [timeText, row.userName, ordersText]
        .filter((part): part is string => !!part)
        .join(' · '),
      actionViews: row.actions.map((action) => ({
        action,
        label: actionLabel(action, row),
        ...ACTION_SKINS[action],
      })),
    };
  }

  const views = computed<ICajaMovementRowView[]>(() => rows.value.map(toView));

  async function runAction(
    action: TCajaMovementAction,
    view: ICajaMovementRowView
  ): Promise<void> {
    // The list may have been refetched under the dialog; act on the current
    // movement instance, and not at all if it is gone.
    const movement = props.movements.find((m) => m.id === view.id);
    if (!movement) return;
    const changed = await run(action, movement);
    if (changed) emit('changed');
  }

  async function openActions(view: ICajaMovementRowView): Promise<void> {
    if (busy.value || !view.actionViews.length) return;
    // The focused row keeps receiving Enter/Space behind the overlay (no focus
    // trap): without this flag each press would stack another dialog.
    dialogOpen.value = true;
    let action: TCajaMovementAction | null;
    try {
      action = await modals.showDynamicDialog<TCajaMovementAction | null>(
        V2CajaMovementActionsModal,
        { view },
        'caja-movement-actions',
        { width: 440 }
      );
    } finally {
      if (!disposed) dialogOpen.value = false;
    }
    if (!action) return;
    await runAction(action, view);
  }
</script>

<template>
  <div ref="rootRef" class="v2-caja-movements-list">
    <V2CajaMovementRow
      v-for="view in views"
      :key="view.id"
      :view="view"
      :wide="wide"
      :busy="busy"
      @open="openActions(view)"
      @action="runAction($event, view)"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-caja-movements-list {
    // Owns the section's remaining height: long sessions scroll here, not the
    // page (the header/hero/footer stay put). The 2px padding (cancelled by
    // the negative margin) keeps the rows' focus ring from being clipped.
    @apply -tw-m-0.5 tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-0.5;
  }
</style>
